import type { QueryRowType } from "@rocicorp/zero";
import {
  getAIProvider,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { canonicalManagedConnectorId } from "../../../shared/aiProviders/managedAiConnector";
import type { queries } from "../../../shared/queries";
import type { AiUsageRecord } from "./aiUsage";
import { recordCost } from "./aiUsage";
import {
  addRecordToActivityBuckets,
  emptyActivityBuckets,
  type ActivityCostBucket,
  type AiUsageActivity,
} from "./aiUsageActivities";

export type MonitorAiProvider = QueryRowType<
  ReturnType<typeof queries.monitorAiProviders>
>;

export type AiProviderModelCostRow = {
  model: string;
  estimatedCostUsd: number | null;
  requestCount: number;
  byActivity: Record<AiUsageActivity, ActivityCostBucket>;
};

export type AiProviderMonitoringRow = {
  id: string;
  label: string;
  providerType: string;
  chatModel: string | null;
  fastModel: string | null;
  active: boolean;
  estimatedCostUsd: number | null;
  requestCount: number;
  modelRows: AiProviderModelCostRow[];
};

export function providerLabel(provider: MonitorAiProvider): string {
  const type = provider.provider
    ? getAIProvider(resolveAIProviderId(provider)).label
    : "AI provider";
  const model = provider.chatModel ?? provider.fastModel;
  return model ? `${type} • ${model}` : type;
}

export function providerModelIds(provider: MonitorAiProvider): Set<string> {
  const models = new Set<string>();
  if (provider.chatModel) models.add(provider.chatModel);
  if (provider.fastModel) models.add(provider.fastModel);
  for (const row of provider.modelPricing ?? []) {
    if (row.modelId) models.add(row.modelId);
  }
  return models;
}

export type ManagedProviderDescriptor = {
  label: string;
  chatModel: string;
  fastModel: string;
};

// An AI-capable connector metered in the ledger with no configured ai_provider
// backing it — surfaced as its own provider row keyed by connector id.
export type ConnectorAiProvider = {
  id: string;
  label: string;
  providerType: string;
};

export function computeAiProviderMonitoringRows(
  providers: readonly MonitorAiProvider[],
  usageRecords: readonly AiUsageRecord[],
  prices: ReadonlyMap<string, number>,
  managed: ManagedProviderDescriptor | null = null,
  connectors: readonly ConnectorAiProvider[] = []
): AiProviderMonitoringRow[] {
  const usageByProvider = new Map<
    string,
    {
      cost: number;
      hasCost: boolean;
      requests: number;
      byModel: Map<string, AiProviderModelCostRow>;
    }
  >();

  for (const record of usageRecords) {
    // Managed/env-default usage carries a null aiProviderId; bucket it under the
    // managed sentinel so it surfaces as the 3B-managed provider row.
    const providerId =
      record.aiProviderId ?? (managed ? MANAGED_AI_PROVIDER_ID : null);
    if (!providerId) continue;
    const bucket = usageByProvider.get(providerId) ?? {
      cost: 0,
      hasCost: false,
      requests: 0,
      byModel: new Map<string, AiProviderModelCostRow>(),
    };
    bucket.requests += record.requestCount ?? 0;
    usageByProvider.set(providerId, bucket);
    // Request-only rows count toward the provider's turns but have no model to
    // break down cost against.
    if (!record.model) continue;
    const cost = recordCost(record, prices);
    if (cost != null) {
      bucket.cost += cost;
      bucket.hasCost = true;
    }
    const modelRow = bucket.byModel.get(record.model) ?? {
      model: record.model,
      estimatedCostUsd: null,
      requestCount: 0,
      byActivity: emptyActivityBuckets(),
    };
    modelRow.requestCount += record.requestCount ?? 0;
    if (cost != null) {
      modelRow.estimatedCostUsd = (modelRow.estimatedCostUsd ?? 0) + cost;
    }
    addRecordToActivityBuckets(modelRow.byActivity, record, cost);
    bucket.byModel.set(record.model, modelRow);
  }

  const rows: AiProviderMonitoringRow[] = providers.map((provider) => {
    const usage = usageByProvider.get(provider.id);
    return {
      id: provider.id,
      label: providerLabel(provider),
      providerType: provider.provider ?? "unknown",
      chatModel: provider.chatModel,
      fastModel: provider.fastModel,
      active: provider.active ?? false,
      estimatedCostUsd: usage?.hasCost ? usage.cost : null,
      requestCount: usage?.requests ?? 0,
      modelRows: [...(usage?.byModel.values() ?? [])]
        .filter((row) => providerModelIds(provider).has(row.model))
        .sort(
          (a, b) =>
            (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
            a.model.localeCompare(b.model)
        ),
    };
  });

  if (managed) {
    const usage = usageByProvider.get(MANAGED_AI_PROVIDER_ID);
    rows.unshift({
      id: MANAGED_AI_PROVIDER_ID,
      label: managed.label,
      providerType: MANAGED_AI_PROVIDER_ID,
      chatModel: managed.chatModel,
      fastModel: managed.fastModel,
      active: false,
      estimatedCostUsd: usage?.hasCost ? usage.cost : null,
      requestCount: usage?.requests ?? 0,
      modelRows: [...(usage?.byModel.values() ?? [])].sort(
        (a, b) =>
          (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
          a.model.localeCompare(b.model)
      ),
    });
  }

  for (const connector of connectors) {
    const usage = usageByProvider.get(connector.id);
    rows.push({
      id: connector.id,
      label: connector.label,
      providerType: connector.providerType,
      chatModel: null,
      fastModel: null,
      active: false,
      estimatedCostUsd: usage?.hasCost ? usage.cost : null,
      requestCount: usage?.requests ?? 0,
      modelRows: [...(usage?.byModel.values() ?? [])].sort(
        (a, b) =>
          (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
          a.model.localeCompare(b.model)
      ),
    });
  }

  return rows;
}

export type UsageSpendBudget = {
  spaceId: string | null;
  connectorId: string | null;
  measure: string;
  limitAmount: number;
};

export type SpendCaps = {
  total: number | null;
  spaces: { id: string; scopeId: string; limitAmount: number }[];
};

// spend_usd caps for the Spend-vs-limits panel: the provider-level total and any
// per-space caps, aggregated over the given connectors. Pass a single connector
// for a provider view, or every surfaced AI connector for the all-providers view
// — a budget on a connector not in this set (an idle or non-AI connector) is
// intentionally excluded. Managed connector ids are canonicalized so
// `3b:managed-ai:*` meters roll up to the one managed budget.
export function spendCapsForConnectors(
  budgets: readonly UsageSpendBudget[],
  connectorIds: readonly string[]
): SpendCaps {
  const wanted = new Set(connectorIds.map(canonicalManagedConnectorId));
  const spend = budgets.filter(
    (b) =>
      b.measure === "spend_usd" &&
      b.connectorId != null &&
      wanted.has(canonicalManagedConnectorId(b.connectorId))
  );
  const total = spend
    .filter((b) => b.spaceId == null)
    .reduce((sum, b) => sum + b.limitAmount, 0);
  const perSpace = new Map<string, number>();
  for (const b of spend) {
    if (b.spaceId == null) continue;
    perSpace.set(b.spaceId, (perSpace.get(b.spaceId) ?? 0) + b.limitAmount);
  }
  return {
    total: total > 0 ? total : null,
    spaces: [...perSpace.entries()].map(([scopeId, limitAmount]) => ({
      id: scopeId,
      scopeId,
      limitAmount,
    })),
  };
}

export function computeModelCostRows(
  records: readonly AiUsageRecord[],
  prices: ReadonlyMap<string, number>
): AiProviderModelCostRow[] {
  const byModel = new Map<string, AiProviderModelCostRow>();
  for (const record of records) {
    if (!record.model) continue;
    const cost = recordCost(record, prices);
    const modelRow = byModel.get(record.model) ?? {
      model: record.model,
      estimatedCostUsd: null,
      requestCount: 0,
      byActivity: emptyActivityBuckets(),
    };
    modelRow.requestCount += record.requestCount ?? 0;
    if (cost != null) {
      modelRow.estimatedCostUsd = (modelRow.estimatedCostUsd ?? 0) + cost;
    }
    addRecordToActivityBuckets(modelRow.byActivity, record, cost);
    byModel.set(record.model, modelRow);
  }
  return [...byModel.values()].sort(
    (a, b) =>
      (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
      a.model.localeCompare(b.model)
  );
}

export type AiProviderAllocationRow = {
  id: string;
  label: string;
  spendUsd: number | null;
  limitAmount: number | null;
  kind: "total" | "space" | "personal" | "unattributed";
  budgetId?: string;
};

export function computeAiProviderAllocationRows(
  providerId: string | null,
  usageRecords: readonly AiUsageRecord[],
  prices: ReadonlyMap<string, number>,
  spaces: readonly { id: string; name: string }[],
  spaceBudgets: readonly {
    id: string;
    scopeId: string;
    limitAmount: number | null;
  }[],
  providerLimitUsd: number | null,
  totalSpendUsd: number | null
): AiProviderAllocationRow[] {
  const rows: AiProviderAllocationRow[] = [];
  if (providerLimitUsd != null) {
    rows.push({
      id: "total",
      label: "Total spend",
      spendUsd: totalSpendUsd,
      limitAmount: providerLimitUsd,
      kind: "total",
    });
  }

  const spendBySpace = new Map<string, number>();
  let personalSpend = 0;
  for (const record of usageRecords) {
    if (providerId != null) {
      const matches =
        providerId === MANAGED_AI_PROVIDER_ID
          ? record.aiProviderId == null
          : record.aiProviderId === providerId;
      if (!matches) continue;
    }
    const cost = recordCost(record, prices);
    if (cost == null) continue;
    const space = record.workflow?.space;
    if (space?.mode === "personal") {
      personalSpend += cost;
      continue;
    }
    if (!space?.id) continue;
    spendBySpace.set(space.id, (spendBySpace.get(space.id) ?? 0) + cost);
  }

  const budgetBySpaceId = new Map(
    spaceBudgets.map((budget) => [budget.scopeId, budget] as const)
  );

  const spaceRows: AiProviderAllocationRow[] = [];
  for (const space of spaces) {
    const budget = budgetBySpaceId.get(space.id);
    const spend = spendBySpace.get(space.id);
    if (spend == null && !budget) continue;
    spaceRows.push({
      id: space.id,
      label: space.name,
      spendUsd: spend ?? null,
      limitAmount: budget?.limitAmount ?? null,
      kind: "space",
      budgetId: budget?.id,
    });
  }
  spaceRows.sort(
    (a, b) =>
      (b.spendUsd ?? 0) - (a.spendUsd ?? 0) || a.label.localeCompare(b.label)
  );

  const aggregateRows: AiProviderAllocationRow[] = [];
  if (personalSpend > 0) {
    aggregateRows.push({
      id: "personal",
      label: "Personal",
      spendUsd: personalSpend,
      limitAmount: null,
      kind: "personal",
    });
  }
  const emittedSpaceSpend = spaceRows.reduce(
    (sum, row) => sum + (row.spendUsd ?? 0),
    0
  );
  const remainder = (totalSpendUsd ?? 0) - emittedSpaceSpend - personalSpend;
  if (remainder > 0.005) {
    aggregateRows.push({
      id: "unattributed",
      label: "Unattributed",
      spendUsd: remainder,
      limitAmount: null,
      kind: "unattributed",
    });
  }

  return [...rows, ...spaceRows, ...aggregateRows];
}
